import type { ViewRecordFieldChange } from '@salescore/client-redux'
import {
  assignInnerRowGeometry,
  getVisibleNodeNames,
  type ModelSearcher,
  type ViewQueryList,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { Result } from 'effect'

import { generateDeleteChangesPure } from '../../recoil/records/mutations/upsertViewRecords/generateDeleteChanges'
import { getNodePure } from '../../state/nodeUtility'
import type { AddNewChildRecordLeft } from '../action/addNewRecord/addNewChildRecord'
import { type DeepReadonly, toMutable } from '../types/DeepReadonly'
import { getChildRecordsWithParent } from './getChildRecordsWithParent'
import type { RecordChange } from './updateViewRecords'

export type DestroyChildRecordLeft =
  | AddNewChildRecordLeft
  | {
      message: 'レコードが存在しません' | 'このブロックは書き込み禁止です'
    }

// eslint-disable-next-line @typescript-eslint/max-params
export function destroyChildRecord(
  { innerRowIndex, nodePath, isDeleteAllChildren }: DeepReadonly<RecordChange>,
  query: DeepReadonly<ViewQueryList>,
  childRecordNode: ViewQueryRecordNode | undefined,
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>,
  modelSearcher: ModelSearcher,
): Result.Result<
  { rootRecordNode: DeepReadonly<ViewQueryRecordNode>; changes: ViewRecordFieldChange[] },
  DestroyChildRecordLeft
> {
  const node = getNodePure(query.tree, nodePath)
  if (node === undefined) {
    return Result.fail({
      message: 'ノードパスが不正です',
    })
  }
  if (childRecordNode === undefined) {
    return Result.fail({
      message: 'レコードが存在しません',
    })
  }

  // ワーニング有無をチェックするため呼び出す。result.successは使わない。
  const result = getChildRecordsWithParent(rootRecordNode, { innerRowIndex, node })
  if (Result.isFailure(result)) {
    return Result.fail(result.failure)
  }

  const childRemovedRecordNode = removeChildInnerRow(rootRecordNode, innerRowIndex, node.path, 0)
  // メタ情報を更新
  const updatedRecordNode = assignInnerRowGeometry(toMutable(childRemovedRecordNode), getVisibleNodeNames(query))

  return Result.succeed({
    rootRecordNode: updatedRecordNode,
    changes: generateDeleteChangesPure(query, node, childRecordNode, modelSearcher, isDeleteAllChildren),
  })
}

function removeChildInnerRow(
  recordNode: DeepReadonly<ViewQueryRecordNode>,
  innerRowIndex: number,
  nodePath: DeepReadonly<string[]>,
  depth: number,
): DeepReadonly<ViewQueryRecordNode> {
  const [path0, path1] = nodePath.slice(depth, -1)

  if (path0 === undefined) return recordNode
  const thisIsParentRecordNode = path1 === undefined

  if (!thisIsParentRecordNode) {
    return {
      ...recordNode,
      children: recordNode.children.map((childTableNode) => ({
        ...childTableNode,
        children:
          childTableNode.nodeName === path1
            ? childTableNode.children.map((childNode) =>
                childNode.meta.innerRowIndexStart <= innerRowIndex && innerRowIndex < childNode.meta.innerRowIndexEnd
                  ? removeChildInnerRow(childNode, innerRowIndex, nodePath, depth + 1)
                  : childNode,
              )
            : childTableNode.children,
      })),
    }
  }

  const currentNodeName = nodePath.at(-1) ?? ''
  const parentRecordNode = recordNode
  return {
    ...parentRecordNode,
    children: parentRecordNode.children.map((tableNode) => ({
      ...tableNode,
      children:
        tableNode.nodeName === currentNodeName
          ? tableNode.children.filter(
              (x) => !(x.meta.innerRowIndexStart <= innerRowIndex && innerRowIndex < x.meta.innerRowIndexEnd),
            )
          : tableNode.children,
    })),
  }
}
